import React from "react";
import { Link } from "react-router-dom";
import PageIntro from "../../../components/admin/PageIntro";
import SectionCard from "../../../components/admin/SectionCard";
import InfoCard from "../../../components/admin/InfoCard";
import { adminTheme } from "../../../components/admin/adminTheme";
import { useHrManagerWorkbench } from "../hooks";
import type { HrManagerWorkbenchResponseDto } from "../hr.types";

const actionLinkStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 14px",
  borderRadius: adminTheme.radius.sm,
  textDecoration: "none",
  background: adminTheme.color.text,
  color: adminTheme.color.pageBg,
  fontWeight: 600,
};

const secondaryLinkStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "10px 14px",
  borderRadius: adminTheme.radius.sm,
  textDecoration: "none",
  background: adminTheme.color.surface,
  color: adminTheme.color.text,
  border: `1px solid ${adminTheme.color.border}`,
  fontWeight: 600,
};

const HrManagerWorkbenchPage: React.FC = () => {
  const { data, isLoading, error } = useHrManagerWorkbench();

  if (isLoading) {
    return (
      <PageIntro
        title="HR Manager Workbench"
        message="Loading HR manager workload…"
      />
    );
  }

  if (error || !data) {
    return (
      <PageIntro
        title="HR Manager Workbench"
        message="Unable to load HR manager dashboard. Please try again."
      />
    );
  }

  const workbench: HrManagerWorkbenchResponseDto = {
    staffTasks: {
      onboardingsToProcess: data.staffTasks?.onboardingsToProcess ?? 0,
      contractsToReview: data.staffTasks?.contractsToReview ?? 0,
      roleChangesPending: data.staffTasks?.roleChangesPending ?? 0,
    },
    caseTasks: {
      newCasesAssigned: data.caseTasks?.newCasesAssigned ?? 0,
      highPriorityCasesAssigned: data.caseTasks?.highPriorityCasesAssigned ?? 0,
      casesNearingSlaBreaches: data.caseTasks?.casesNearingSlaBreaches ?? 0,
    },
    trainingTasks: {
      staffToEnroll: data.trainingTasks?.staffToEnroll ?? 0,
      trainingCompletionsToReview:
        data.trainingTasks?.trainingCompletionsToReview ?? 0,
      overdueMandatoryTrainingFollowUps:
        data.trainingTasks?.overdueMandatoryTrainingFollowUps ?? 0,
    },
  };

  const { staffTasks, caseTasks, trainingTasks } = workbench;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: adminTheme.space.section,
        padding: adminTheme.space.section,
        background: adminTheme.color.pageBg,
        minHeight: "100%",
      }}
    >
      <PageIntro
        title="HR Manager Workbench"
        message="Employee onboarding, staff task execution, HR case handling, and training follow-up."
      />

      <SectionCard title="Employee onboarding">
        <InfoCard
          title="New hire setup"
          body={`Onboardings to process: ${staffTasks.onboardingsToProcess}, contracts to review: ${staffTasks.contractsToReview}, role changes pending: ${staffTasks.roleChangesPending}.`}
        />
        <div
          style={{
            marginTop: adminTheme.space.md,
            display: "flex",
            flexWrap: "wrap",
            gap: adminTheme.space.sm,
          }}
        >
          <Link to="/staff/dashboard/hr/onboarding" style={actionLinkStyle}>
            Create & onboard employee
          </Link>
          <Link to="/staff/dashboard/staff" style={secondaryLinkStyle}>
            Open staff records
          </Link>
        </div>
      </SectionCard>

      <SectionCard title="HR case workload">
        <InfoCard
          title="Case handling"
          body={`New cases assigned: ${caseTasks.newCasesAssigned}, high-priority cases: ${caseTasks.highPriorityCasesAssigned}, cases nearing SLA breach: ${caseTasks.casesNearingSlaBreaches}.`}
        />
      </SectionCard>

      <SectionCard title="Training & follow-up">
        <InfoCard
          title="Training actions"
          body={`Staff to enroll: ${trainingTasks.staffToEnroll}, completions to review: ${trainingTasks.trainingCompletionsToReview}, overdue mandatory training follow-ups: ${trainingTasks.overdueMandatoryTrainingFollowUps}.`}
        />
      </SectionCard>
    </div>
  );
};

export default HrManagerWorkbenchPage;